import React, { useState, useEffect } from 'react';
import {
  Stack,
  Title,
  Text,
  Badge,
  Button,
  Group,
  Alert,
  Loader,
  Center,
  Paper,
  Radio,
  Divider,
  Progress,
  Anchor,
  ActionIcon,
  Box,
  ScrollArea
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconVideo,
  IconLivePhoto,
  IconQuestionMark,
  IconDownload,
  IconCheck,
  IconX,
  IconArrowLeft
} from '@tabler/icons-react';
import { memberDashboard } from '../../services/memberDashboard';
/* import SecureVideoPlayer from '../SecureVideoPlayer'; */
import PlyrPlayer from '../PlyrPlayer';
/* import VideoJSPlayer from '../VideoJSPlayer'; */
/* import AdaptiveVideoPlayer from '../AdaptiveVideoPlayer'; */
import AdaptivePlyrPlayer from '../AdaptivePlyrPlayerv2';

const ClassViewer = ({ classItem, onClose, onProgress }) => {
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quizMode, setQuizMode] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);

  useEffect(() => {
    loadClass();

    // Add history state to handle back button
    window.history.pushState({ classView: true }, '', window.location.href);

    // Handle back button override
    const handlePopState = (event) => {
      if (event.state?.classView) {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Clean up history state
      if (window.history.state?.classView) {
        window.history.back();
      }
    };
  }, [classItem.class.id, onClose]);

  const loadClass = async () => {
    try {
      const data = await memberDashboard.getClass(classItem.class.id);
      setClassData(data);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to load class',
        color: 'red',
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleQuizSubmit = async () => {
    const quiz = classData.class.quiz_json;
    const answers = quiz.map((_, index) => quizAnswers[index]);

    // Check if all questions are answered
    if (answers.includes(undefined)) {
      notifications.show({
        title: 'Incomplete Quiz',
        message: 'Please answer all questions before submitting',
        color: 'yellow',
      });
      return;
    }

    setSubmittingQuiz(true);
    try {
      const result = await memberDashboard.submitQuiz(classItem.class.id, answers);
      setQuizResult(result);
      onProgress(); // Refresh dashboard
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to submit quiz',
        color: 'red',
      });
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleMarkComplete = async () => {
    setMarkingComplete(true);
    try {
      await memberDashboard.markCompleted(classItem.class.id);
      notifications.show({
        title: 'Success',
        message: 'Class marked as completed',
        color: 'green',
      });
      onProgress(); // Refresh dashboard
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to mark class as complete',
        color: 'red',
      });
    } finally {
      setMarkingComplete(false);
    }
  };

  const renderVideoPlayer = () => {
    if (!classData.vdocipher) {
      return (
        <Alert color="red">
          Failed to load video player. Please try refreshing.
        </Alert>
      );
    }

    const { otp, playbackInfo } = classData.vdocipher;

    const isDesktop = window.innerWidth >= 768; // or any breakpoint you define

    return (
      <div
        style={{
          position: 'relative',
          paddingBottom: isDesktop ? '26.25%' : '56.25%',
          height: 0,
          maxWidth: isDesktop ? '800px' : '', // set your desired max width

        }}
      >
        <iframe
          src={`https://player.vdocipher.com/v2/?otp=${otp}&playbackInfo=${playbackInfo}`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: isDesktop ? '100%' : '100%',
            border: 0
          }}
          allow="encrypted-media"
          allowFullScreen
        />
      </div>
    );

  };

  const renderAdaptivePlayer = () => {
    if (!classData.class.hls_link) {
      return (
        <Alert color="red">
          ERROR CODE 404:HLS not defined. Failed to load video player. Please contact support.
        </Alert>
      );
    }

    const isDesktop = window.innerWidth >= 768; // or any breakpoint you define

    return (
      <div
        style={{
          /* position: 'relative', *//* 
          paddingBottom: isDesktop ? '30.25%' : '56.25%', */
          /*  height: 0, */
          maxWidth: isDesktop ? '800px' : '', // set your desired max width
        }}
      >
        <AdaptivePlyrPlayer
          videoId={classData.class.hls_link}
          title={classData.class.title}
          debug={false}  // Shows console logs and debug overlay
        />
      </div>
    );

  };




  const renderHLSVideoPlayer = () => {
    if (!classData.class.hls_link) {
      return (
        <Alert color="red">
          ERROR CODE 404:HLS not defined. Failed to load video player. Please contact support.
        </Alert>
      );
    }

    const isDesktop = window.innerWidth >= 768; // or any breakpoint you define

    return (
      <div
        style={{
          position: 'relative',
          paddingBottom: isDesktop ? '30.25%' : '56.25%',
          height: 0,
          maxWidth: isDesktop ? '800px' : '', // set your desired max width
        }}
      >
        <PlyrPlayer
          videoPath={classData.class.hls_link}
          title={classData.class.title}
        />
      </div>
    );

  };

  const renderQuiz = () => {
    const quiz = classData.class.quiz_json;

    return (
      <Stack>
        <Group justify="space-between">
          <Title order={4}>Class Quiz</Title>
          <Badge color="purple">
            Pass Rate: {classData.class.quiz_pass_rate}%
          </Badge>
        </Group>

        {quiz.map((question, index) => (
          <Paper key={index} withBorder p="md">
            <Stack>
              <Text fw={600}>
                Question {index + 1}: {question.question}
              </Text>

              <Radio.Group
                value={quizAnswers[index]?.toString()}
                onChange={(value) => setQuizAnswers(prev => ({
                  ...prev,
                  [index]: parseInt(value)
                }))}
              >
                {question.options.map((option, optIndex) => (
                  <Radio
                    key={optIndex}
                    value={optIndex.toString()}
                    label={`${String.fromCharCode(65 + optIndex)}. ${option}`}
                  />
                ))}
              </Radio.Group>
            </Stack>
          </Paper>
        ))}

        <Button
          onClick={handleQuizSubmit}
          loading={submittingQuiz}
          disabled={Object.keys(quizAnswers).length !== quiz.length}
          size="lg"
        >
          Submit Quiz
        </Button>
      </Stack>
    );
  };

  const renderQuizResult = () => {
    const quiz = classData.class.quiz_json;

    return (
      <Stack>
        <Group justify="space-between">
          <Title order={4}>Quiz Results</Title>
          <Badge color={quizResult.passed ? 'green' : 'red'} size="lg">
            {quizResult.score}% {quizResult.passed ? 'PASSED' : 'FAILED'}
          </Badge>
        </Group>

        <Progress
          value={quizResult.score}
          color={quizResult.passed ? 'green' : 'red'}
          size="xl"
          label={`${quizResult.score}%`}
        />

        <Text size="sm" c="dimmed">
          You need {quizResult.pass_rate}% to pass. This was attempt #{quizResult.attempts_count}.
        </Text>

        {quizResult.results.map((result, index) => (
          <Paper key={index} withBorder p="md">
            <Stack gap="xs">
              <Group>
                <Text fw={600}>Question {index + 1}</Text>
                {result.is_correct ? (
                  <Badge color="green" leftSection={<IconCheck size={12} />}>Correct</Badge>
                ) : (
                  <Badge color="red" leftSection={<IconX size={12} />}>Incorrect</Badge>
                )}
              </Group>

              <Text size="sm">{quiz[index].question}</Text>

              <Group>
                <Text size="sm">Your answer: {String.fromCharCode(65 + result.user_answer)}</Text>
                <Text size="sm">Correct answer: {String.fromCharCode(65 + result.correct_answer)}</Text>
              </Group>

              {result.explanation && (
                <Alert color="blue" p="xs">
                  <Text size="sm">{result.explanation}</Text>
                </Alert>
              )}
            </Stack>
          </Paper>
        ))}

        {!quizResult.passed && (
          <Button
            onClick={() => {
              setQuizResult(null);
              setQuizAnswers({});
            }}
            variant="light"
            size="lg"
          >
            Fail - Start Quiz
          </Button>
        )}



      </Stack >
    );
  };

  if (loading) {
    return (
      <Box
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--mantine-color-body)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loader size="lg" />
      </Box>
    );
  }

  if (!classData) {
    return (
      <Box
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'var(--mantine-color-body)',
          zIndex: 1000,
          padding: '20px'
        }}
      >
        <Alert color="red">Failed to load class data</Alert>
      </Box>
    );
  }

  const isVideoClass = classData.class.type === 'video_class';
  const hasQuiz = isVideoClass && classData.class.quiz_json && classData.class.quiz_json.length > 0;
  const quizPassed = classData.progress?.quiz_passed;
  const isCompleted = classData.progress?.is_completed;

  //console.log('isCompleted - ', isCompleted);

  let is_hls_available = false;
  if (classData.class.hls_link) {
    if (!classData.class.hls_link.includes('dis')) {
      is_hls_available = true;
    }
  }
  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--mantine-color-body)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Paper withBorder p="md" style={{ borderRadius: 0, borderLeft: 0, borderRight: 0, borderTop: 0 }}>
        <Group justify="space-between">
          <Group>
            <ActionIcon
              variant="light"
              onClick={onClose}
              size="lg"
            >
              <IconArrowLeft size={18} />
            </ActionIcon>
            <Title order={3} lineClamp={1}>{classData.class.title}</Title>
          </Group>

          <Group>
            <Badge color={isVideoClass ? 'blue' : 'green'} leftSection={
              isVideoClass ? <IconVideo size={12} /> : <IconLivePhoto size={12} />
            }>
              {isVideoClass ? 'Video' : 'Live'}
            </Badge>

            {isCompleted && (
              <Badge color="green" leftSection={<IconCheck size={12} />}>
                Completed
              </Badge>
            )}
          </Group>
        </Group>
      </Paper>

      {/* Content */}
      <ScrollArea style={{ flex: 1 }} p="md">
        <Stack pb={hasQuiz && !quizResult && !quizMode ? 80 : 60}>
          <Text>{classData.class.short_description}</Text>

          {/* <Divider label="Class Content" /> */}

          {isVideoClass ? (
            <>
              {/* {renderVideoPlayer()} */}
              {/*  {is_hls_available ? renderHLSVideoPlayer() : renderVideoPlayer()} */}

              {is_hls_available ? renderAdaptivePlayer() : renderVideoPlayer()}

              {/* Downloadable Materials */}
              {classData.class.downloadable_material_links_json &&
                classData.class.downloadable_material_links_json.length > 0 && (
                  <>
                    <Divider label="Downloadable Materials" />
                    <Stack>
                      {classData.class.downloadable_material_links_json.map((material, index) => (
                        <Paper key={index} withBorder p="md">
                          <Group justify="space-between">
                            <Group>
                              <IconDownload size={20} />
                              <Stack gap="xs">
                                <Text fw={600} size="sm">{material.filename}</Text>
                                <Text size="xs" c="dimmed">
                                  Size: {(material.size / 1024 / 1024).toFixed(2)} MB
                                </Text>
                              </Stack>
                            </Group>
                            <Anchor href={material.url} target="_blank">
                              <Button size="xs" leftSection={<IconDownload size={14} />}>
                                Download
                              </Button>
                            </Anchor>
                          </Group>
                        </Paper>
                      ))}
                    </Stack>
                  </>
                )}

              {/* Quiz Section */}
              {hasQuiz && (
                <>
                  <Divider label="Class Quiz" />

                  {quizResult ? (
                    renderQuizResult()
                  ) : quizMode ? (
                    renderQuiz()
                  ) : (
                    <Paper withBorder p="md">
                      <Stack>
                        <Group>
                          <IconQuestionMark size={24} />
                          <Stack gap="xs">
                            <Text fw={600}>Complete the Quiz</Text>
                            <Text size="sm" c="dimmed">
                              Answer {classData.class.quiz_json.length} questions to complete this class
                            </Text>
                            <Text size="sm" c="dimmed">
                              Pass rate: {classData.class.quiz_pass_rate}%
                            </Text>
                          </Stack>
                        </Group>

                        {classData.progress?.quiz_attempts && classData.progress.quiz_attempts.length > 0 && (
                          <Alert color="blue">
                            <Text size="sm">
                              Previous attempts: {classData.progress.quiz_attempts.length}
                              {quizPassed && ' - Quiz Passed!'}
                            </Text>
                          </Alert>
                        )}
                      </Stack>
                    </Paper>
                  )}
                </>
              )}
            </>
          ) : (
            // Live Class
            <Paper withBorder p="md">
              <Stack>
                <Group>
                  <IconLivePhoto size={24} />
                  <Text fw={600}>Live Class Link</Text>
                </Group>

                <Anchor href={classData.class.live_link} target="_blank">
                  <Button fullWidth leftSection={<IconLivePhoto size={16} />} size="lg">
                    Join Live Class
                  </Button>
                </Anchor>

                {classData.class.passkey && (
                  <Alert>
                    <Text size="sm">
                      Passkey: <Text component="span" fw={600}>{classData.class.passkey}</Text>
                    </Text>
                  </Alert>
                )}
              </Stack>
            </Paper>
          )}

          {classData.class.long_description && (
            <>
              <Divider label="Description" />
              <Text size="sm">{classData.class.long_description}</Text>
            </>
          )}
        </Stack>
      </ScrollArea>

      {/* Fixed Bottom Buttons */}
      <Paper
        withBorder
        p="md"
        style={{
          borderRadius: 0,
          borderLeft: 0,
          borderRight: 0,
          borderBottom: 0,
          backgroundColor: 'var(--mantine-color-body)'
        }}
      >
        {/* Video Class with Quiz - Not Started */}
        {isVideoClass && hasQuiz && !quizResult && !quizMode && (
          <Button
            onClick={() => setQuizMode(true)}
            fullWidth
            size="lg"
            leftSection={<IconCheck size={16} />}
          >
            {quizPassed ? 'Retake Quiz' : 'Start Quiz'}
          </Button>
        )}

        {/* Live Class - Not Completed */}
        {!isVideoClass && !isCompleted && (
          <Button
            onClick={handleMarkComplete}
            loading={markingComplete}
            color="green"
            fullWidth
            size="lg"
            leftSection={<IconCheck size={16} />}
          >
            Mark as Completed
          </Button>
        )}

        {/* Video Class without Quiz - Not Completed */}
        {isVideoClass && !hasQuiz && !isCompleted && (
          <Button
            onClick={handleMarkComplete}
            loading={markingComplete}
            color="green"
            fullWidth
            size="lg"
            leftSection={<IconCheck size={16} />}
          >
            Mark as Completed
          </Button>
        )}

        {/* Quiz Passed - Not Completed */}
        {isVideoClass && hasQuiz && quizPassed && !isCompleted && (
          <Button
            onClick={handleMarkComplete}
            loading={markingComplete}
            color="green"
            fullWidth
            size="lg"
            leftSection={<IconCheck size={16} />}
          >
            Mark as Completed
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default ClassViewer;